import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Automatisation.scss';
import SEO from '../../components/SEO';

const N8N_WEBHOOK = 'https://n8n.kanemx.com/webhook/devis-automatisation';


const GridCanvas = () => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animId;
        let W, H, t = 0;

        const resize = () => {
            W = canvas.width = window.innerWidth;
            H = canvas.height = window.innerHeight;
        };

        const draw = () => {
            ctx.clearRect(0, 0, W, H);
            t += 0.003;
            const cols = Math.floor(W / 60);
            const rows = Math.floor(H / 60);
            for (let i = 0; i <= cols; i++) {
                for (let j = 0; j <= rows; j++) {
                    const x = i * 60;
                    const y = j * 60;
                    const dist = Math.sin(i * 0.3 + t) * Math.cos(j * 0.3 + t);
                    const alpha = (dist + 1) / 2 * 0.12;
                    ctx.beginPath();
                    ctx.arc(x, y, 1.2, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(201,168,76,${alpha})`;
                    ctx.fill();
                }
            }
            for (let i = 0; i < 3; i++) {
                const y = H * (0.2 + i * 0.3) + Math.sin(t + i) * 40;
                const grad = ctx.createLinearGradient(0, y, W, y);
                grad.addColorStop(0, 'transparent');
                grad.addColorStop(0.3 + Math.sin(t + i) * 0.2, `rgba(201,168,76,0.06)`);
                grad.addColorStop(0.7, `rgba(79,142,247,0.04)`);
                grad.addColorStop(1, 'transparent');
                ctx.beginPath();
                ctx.strokeStyle = grad;
                ctx.lineWidth = 1;
                ctx.moveTo(0, y);
                for (let x = 0; x < W; x += 4) {
                    ctx.lineTo(x, y + Math.sin(x * 0.01 + t + i) * 20);
                }
                ctx.stroke();
            }
            animId = requestAnimationFrame(draw);
        };

        resize();
        draw();
        window.addEventListener('resize', resize);
        return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
    }, []);

    return <canvas ref={canvasRef} className="auto-canvas" />;
};

const Orbs = () => (
    <div className="auto-orbs">
        <div className="auto-orb auto-orb-1" />
        <div className="auto-orb auto-orb-2" />
        <div className="auto-orb auto-orb-3" />
    </div>
);

const Typewriter = ({ words }) => {
    const [index, setIndex] = useState(0);
    const [displayed, setDisplayed] = useState('');
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!Array.isArray(words) || words.length === 0) return;
        const word = words[index % words.length];
        let timeout;
        if (!deleting && displayed.length < word.length) {
            timeout = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), 80);
        } else if (!deleting && displayed.length === word.length) {
            timeout = setTimeout(() => setDeleting(true), 2000);
        } else if (deleting && displayed.length > 0) {
            timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 40);
        } else {
            setDeleting(false);
            setIndex(i => i + 1);
        }
        return () => clearTimeout(timeout);
    }, [displayed, deleting, index, words]);

    return (
        <span className="auto-typewriter">
            {displayed}<span className="auto-cursor">|</span>
        </span>
    );
};

const FlowDiagram = () => {
    const nodes = [
        { label: 'Calendly', icon: '📅', x: 12, y: 18 },
        { label: 'Gmail', icon: '✉', x: 12, y: 50 },
        { label: 'WhatsApp', icon: '💬', x: 12, y: 82 },
        { label: 'KANEM AI', icon: '⬡', x: 50, y: 50, center: true },
        { label: 'Notion', icon: '📋', x: 88, y: 18 },
        { label: 'CRM', icon: '👥', x: 88, y: 50 },
        { label: 'Rapport', icon: '📊', x: 88, y: 82 },
    ];

    const connections = [
        { x1: 12, y1: 18, x2: 50, y2: 50 },
        { x1: 12, y1: 50, x2: 50, y2: 50 },
        { x1: 12, y1: 82, x2: 50, y2: 50 },
        { x1: 50, y1: 50, x2: 88, y2: 18 },
        { x1: 50, y1: 50, x2: 88, y2: 50 },
        { x1: 50, y1: 50, x2: 88, y2: 82 },
    ];

    return (
        <div className="auto-flow">
            <svg className="auto-flow-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                {connections.map((c, i) => (
                    <line
                        key={i}
                        x1={`${c.x1}%`} y1={`${c.y1}%`}
                        x2={`${c.x2}%`} y2={`${c.y2}%`}
                        stroke="rgba(201,168,76,0.25)"
                        strokeWidth="0.4"
                        strokeDasharray="3 3"
                        className="auto-flow-line"
                        style={{ animationDelay: `${i * 0.2}s` }}
                    />
                ))}
            </svg>
            {nodes.map((n, i) => (
                <div
                    key={i}
                    className={`auto-flow-node ${n.center ? 'center' : ''}`}
                    style={{ left: `${n.x}%`, top: `${n.y}%`, animationDelay: `${i * 0.15}s` }}
                >
                    <span className="auto-flow-icon">{n.icon}</span>
                    <span className="auto-flow-label">{n.label}</span>
                </div>
            ))}
        </div>
    );
};

const Counter = ({ target, suffix = '', prefix = '' }) => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            let start = 0;
            const step = () => {
                start += target / 60;
                if (start >= target) { el.textContent = prefix + Math.ceil(target) + suffix; return; }
                el.textContent = prefix + Math.floor(start) + suffix;
                requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        }, { threshold: 0.5 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [target, suffix, prefix]);
    return <span ref={ref}>{prefix}0{suffix}</span>;
};

const Automatisation = () => {
    const { t } = useTranslation();
    const [heroReady, setHeroReady] = useState(false);
    const [currency, setCurrency] = useState('CAD');
    const [step, setStep] = useState(1);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const sectionsRef = useRef([]);

    const [form, setForm] = useState({
        nom: '', email: '', telephone: '',
        services: [], outils: '', budget: '', delai: '', message: ''
    });

    const typewriterWords = t('automatisation.typewriter', { returnObjects: true });
    const servicesList = t('automatisation.services', { returnObjects: true });
    const statsList = t('automatisation.stats', { returnObjects: true });
    const stepsList = t('automatisation.steps', { returnObjects: true });
    const marqueeItems = t('automatisation.marquee', { returnObjects: true });
    const serviceOptions = t('automatisation.services_options', { returnObjects: true });
    const formFeatures = t('automatisation.form_features', { returnObjects: true });
    const budgetOptions = t('automatisation.budget_options', { returnObjects: true });
    const delaiOptions = t('automatisation.delai_options', { returnObjects: true });
    const plansList = t('automatisation.plans', { returnObjects: true });

    const customPrice = t('automatisation.custom_price');
    const plansBase = [
        { xof_setup: '250 000', xof_monthly: '37 500', cad_setup: '500', cad_monthly: '75', featured: false },
        { xof_setup: '600 000', xof_monthly: '75 000', cad_setup: '1 200', cad_monthly: '150', featured: true },
        { xof_setup: customPrice, xof_monthly: customPrice, cad_setup: customPrice, cad_monthly: customPrice, featured: false },
    ];


    useEffect(() => {
        window.scrollTo(0, 0);
        const timer = setTimeout(() => setHeroReady(true), 200);
        const obs = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('auto-visible'); }),
            { threshold: 0.08 }
        );
        sectionsRef.current.forEach(el => el && obs.observe(el));
        return () => { clearTimeout(timer); obs.disconnect(); };
    }, []);

    const addRef = i => el => sectionsRef.current[i] = el;
    const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
    const handleServiceToggle = val => setForm({
        ...form,
        services: form.services.includes(val)
            ? form.services.filter(v => v !== val)
            : [...form.services, val]
    });

    const validateStep = () => {
        const newErrors = {};
        if (step === 1) {
            if (!form.nom) newErrors.nom = t('form.required');
            if (!form.email) newErrors.email = t('form.required');
        }
        if (step === 2 && !form.services.length) newErrors.services = t('automatisation.services_label2');
        if (step === 3) {
            if (!form.budget) newErrors.budget = t('form.required');
            if (!form.delai) newErrors.delai = t('form.required');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = next => { if (validateStep()) setStep(next); };

    const handleSubmit = async e => {
        e.preventDefault();
        if (!validateStep()) return;
        setLoading(true);
        try {
            await fetch(N8N_WEBHOOK, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, service: 'Automatisation', dateSoumission: new Date().toISOString() })
            });
            setStatus('success');
        } catch { setStatus('error'); }
        setLoading(false);
    };

    return (
        <div className="auto-page">
            <SEO title="Automatisations IA" description="Automatisations intelligentes pour votre business." url="/solutions/automatisation" />

            <section className="auto-hero">
                <GridCanvas />
                <Orbs />
                <div className="auto-hero-vignette" />

                <div className="auto-hero-content">
                    <div className={`auto-badge ${heroReady ? 'in' : ''}`}>
                        <span className="auto-badge-dot" />
                        {t('automatisation.badge')}
                    </div>

                    <h1 className="auto-hero-title">
                        <div className="auto-reveal-line">
                            <span className="auto-reveal-inner" style={{
                                transitionDelay: '0.3s',
                                transform: heroReady ? 'translateY(0)' : 'translateY(110%)',
                                opacity: heroReady ? 1 : 0,
                            }}>
                                {t('automatisation.hero_line1')}
                            </span>
                        </div>
                        <div className="auto-reveal-line accent">
                            <span className="auto-reveal-inner" style={{
                                transitionDelay: '0.48s',
                                transform: heroReady ? 'translateY(0)' : 'translateY(110%)',
                                opacity: heroReady ? 1 : 0,
                            }}>
                                <Typewriter words={Array.isArray(typewriterWords) ? typewriterWords : []} />
                            </span>
                        </div>
                        <div className="auto-reveal-line">
                            <span className="auto-reveal-inner" style={{
                                transitionDelay: '0.62s',
                                transform: heroReady ? 'translateY(0)' : 'translateY(110%)',
                                opacity: heroReady ? 1 : 0,
                            }}>
                                {t('automatisation.hero_line3')}
                            </span>
                        </div>
                    </h1>

                    <div className={`auto-hero-sub ${heroReady ? 'in' : ''}`} style={{ transitionDelay: '0.9s' }}>
                        {t('automatisation.hero_sub')}
                    </div>

                    <div className={`auto-hero-actions ${heroReady ? 'in' : ''}`} style={{ transitionDelay: '1.1s' }}>
                        <a href="#devis" className="auto-btn-primary">
                            {t('automatisation.cta_devis')} <span>→</span>
                        </a>
                        <a href="#services" className="auto-btn-ghost">
                            {t('automatisation.cta_services')}
                        </a>
                    </div>
                </div>

                <div className={`auto-hero-diagram ${heroReady ? 'in' : ''}`} style={{ transitionDelay: '0.8s' }}>
                    <FlowDiagram />
                </div>

                <div className="auto-scroll-indicator">
                    <div className="auto-scroll-mouse"><div className="auto-scroll-wheel" /></div>
                </div>
            </section>

            <div className="auto-marquee-wrap">
                <div className="auto-marquee">
                    {[...Array(2)].flatMap((_, ri) =>
                        Array.isArray(marqueeItems) && marqueeItems.map((item, i) => (
                            <span key={`${ri}-${i}`} className="auto-marquee-item">{item} <em>·</em></span>
                        ))
                    )}
                </div>
            </div>

            <section className="auto-section auto-reveal" id="services" ref={addRef(0)}>
                <div className="auto-inner">
                    <p className="auto-label">{t('automatisation.services_label')}</p>
                    <h2 className="auto-title">{t('automatisation.services_title')}</h2>
                    <div className="auto-service-grid">
                        {Array.isArray(servicesList) && servicesList.map((s, i) => (
                            <div className="auto-service-card" key={i} style={{ '--c': s.color, animationDelay: `${i * 0.1}s` }}>
                                <div className="auto-service-card-glow" />
                                <div className="auto-service-icon">{s.icon}</div>
                                <h3>{s.title}</h3>
                                <p>{s.desc}</p>
                                <div className="auto-tags">
                                    {Array.isArray(s.tags) && s.tags.map(tag => <span key={tag}>{tag}</span>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="auto-stats-section auto-reveal" ref={addRef(1)}>
                <div className="auto-inner auto-stats-grid">
                    {Array.isArray(statsList) && statsList.map((s, i) => (
                        <div className="auto-stat" key={i}>
                            <div className="auto-stat-num">
                                <Counter target={s.val} suffix={s.suffix} prefix={s.prefix || ''} />
                            </div>
                            <p>{s.label}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="auto-section auto-reveal" ref={addRef(2)}>
                <div className="auto-inner">
                    <p className="auto-label">{t('automatisation.process_label')}</p>
                    <h2 className="auto-title">{t('automatisation.process_title')}</h2>
                    <div className="auto-process">
                        {Array.isArray(stepsList) && stepsList.map((s, i) => (
                            <div className="auto-process-step" key={s.num} style={{ transitionDelay: `${i * 0.1}s` }}>
                                <div className="auto-process-num">{s.num}</div>
                                <div className="auto-process-line" />
                                <h3>{s.title}</h3>
                                <p>{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="auto-section auto-reveal" ref={addRef(3)}>
                <div className="auto-inner">
                    <p className="auto-label">{t('automatisation.pricing_label')}</p>
                    <div className="auto-pricing-header">
                        <h2 className="auto-title" style={{ marginBottom: 0 }}>
                            {t('automatisation.pricing_title')}
                        </h2>
                        <div className="auto-currency-toggle">
                            <button className={currency === 'CAD' ? 'active' : ''} onClick={() => setCurrency('CAD')}>CAD</button>
                            <button className={currency === 'XOF' ? 'active' : ''} onClick={() => setCurrency('XOF')}>XOF</button>
                        </div>
                    </div>

                    <div className="auto-pricing-grid">
                        {Array.isArray(plansList) && plansList.map((p, i) => {
                            const base = plansBase[i];
                            const setup = currency === 'CAD' ? base.cad_setup : base.xof_setup;
                            const monthly = currency === 'CAD' ? base.cad_monthly : base.xof_monthly;
                            const isCustom = setup === customPrice;

                            return (
                                <div className={`auto-pricing-card ${base.featured ? 'featured' : ''}`} key={p.name}>
                                    {base.featured && <div className="auto-featured-badge">{t('automatisation.recommended')}</div>}
                                    <h3>{p.name}</h3>
                                    <div className="auto-price-block">
                                        {isCustom ? (
                                            <div className="auto-price-custom">{t('automatisation.custom_price')}</div>

                                        ) : (
                                            <>
                                                <div className="auto-price-setup">
                                                    <span className="auto-price-amount">{setup}</span>
                                                    <span className="auto-price-currency">{currency}</span>
                                                    <span className="auto-price-label">setup</span>
                                                </div>
                                                <div className="auto-price-monthly">+ {monthly} {currency}/mois</div>
                                            </>
                                        )}
                                    </div>
                                    <ul>
                                        {Array.isArray(p.features) && p.features.map(f => (
                                            <li key={f}><span className="auto-check">✓</span>{f}</li>
                                        ))}
                                    </ul>
                                    <a href="#devis" className={`auto-pricing-btn ${base.featured ? 'featured' : ''}`}>
                                        {t('automatisation.quote_btn')}
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                    <p className="auto-pricing-note">{t('automatisation.pricing_note')}</p>
                </div>
            </section>

            <section className="auto-section auto-reveal" id="devis" ref={addRef(4)}>
                <div className="auto-inner">
                    <div className="auto-form-wrap">
                        <div className="auto-form-left">
                            <p className="auto-label">{t('automatisation.form_label')}</p>
                            <h2>{t('automatisation.form_title')}</h2>
                            <p className="auto-form-sub">{t('automatisation.form_sub')}</p>
                            <div className="auto-form-features">
                                {Array.isArray(formFeatures) && formFeatures.map(f => (
                                    <div key={f} className="auto-form-feature">
                                        <span>✓</span>{f}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="auto-form-right">
                            {status === 'success' ? (
                                <div className="auto-form-success">
                                    <div className="auto-success-icon">✓</div>
                                    <h3>{t('automatisation.success_title')}</h3>
                                    <p>{t('automatisation.success_desc')}</p>
                                    <button onClick={() => { setStatus(null); setStep(1); }} className="auto-btn-primary">
                                        {t('automatisation.new_request')}
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    <div className="auto-form-indicator">
                                        {[1, 2, 3].map(s => (
                                            <div key={s} className={`auto-form-dot ${step >= s ? 'active' : ''}`}>
                                                <span>{s}</span>
                                                {s < 3 && <div className="auto-form-dot-line" />}
                                            </div>
                                        ))}
                                    </div>

                                    {step === 1 && (
                                        <div className="auto-form-step">
                                            <h3>{t('automatisation.step1_title')}</h3>
                                            <div className="auto-form-row">
                                                <div className="auto-form-group">
                                                    <label>{t('form.name')}</label>
                                                    <input type="text" name="nom" value={form.nom} onChange={handleChange} placeholder="Jean Dupont" />
                                                    {errors.nom && <span className="auto-error">{errors.nom}</span>}
                                                </div>
                                                <div className="auto-form-group">
                                                    <label>{t('form.email')}</label>
                                                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="jean@exemple.com" />
                                                    {errors.email && <span className="auto-error">{errors.email}</span>}
                                                </div>
                                            </div>
                                            <div className="auto-form-group">
                                                <label>{t('form.phone')}</label>
                                                <input type="tel" name="telephone" value={form.telephone} onChange={handleChange} placeholder="+1 514 000 0000" />
                                            </div>
                                            <button type="button" className="auto-btn-primary" onClick={() => handleNext(2)}>
                                                {t('form.continue')}
                                            </button>
                                        </div>
                                    )}

                                    {step === 2 && (
                                        <div className="auto-form-step">
                                            <h3>{t('automatisation.step2_title')}</h3>
                                            <div className="auto-form-group">
                                                <label>{t('automatisation.services_label2')}</label>
                                                <div className="auto-checkbox-grid">
                                                    {Array.isArray(serviceOptions) && serviceOptions.map(s => (
                                                        <label key={s} className={`auto-checkbox-item ${form.services.includes(s) ? 'checked' : ''}`}>
                                                            <input type="checkbox" checked={form.services.includes(s)} onChange={() => handleServiceToggle(s)} />
                                                            {s}
                                                        </label>
                                                    ))}
                                                </div>
                                                {errors.services && <span className="auto-error">{errors.services}</span>}
                                            </div>
                                            <div className="auto-form-group">
                                                <label>{t('automatisation.outils_label')}</label>
                                                <input type="text" name="outils" value={form.outils} onChange={handleChange} placeholder={t('automatisation.outils_placeholder')} />
                                            </div>
                                            <div className="auto-form-nav">
                                                <button type="button" className="auto-back-btn" onClick={() => setStep(1)}>← {t('form.back')}</button>
                                                <button type="button" className="auto-btn-primary" onClick={() => handleNext(3)}>{t('form.continue')}</button>
                                            </div>
                                        </div>
                                    )}

                                    {step === 3 && (
                                        <div className="auto-form-step">
                                            <h3>{t('automatisation.step3_title')}</h3>
                                            <div className="auto-form-row">
                                                <div className="auto-form-group">
                                                    <label>{t('automatisation.budget_label')}</label>
                                                    <select name="budget" value={form.budget} onChange={handleChange}>
                                                        <option value="">{t('form.select')}</option>
                                                        {Array.isArray(budgetOptions) && budgetOptions.map(o => <option key={o}>{o}</option>)}
                                                    </select>
                                                    {errors.budget && <span className="auto-error">{errors.budget}</span>}
                                                </div>
                                                <div className="auto-form-group">
                                                    <label>{t('automatisation.delai_label')}</label>
                                                    <select name="delai" value={form.delai} onChange={handleChange}>
                                                        <option value="">{t('form.select')}</option>
                                                        {Array.isArray(delaiOptions) && delaiOptions.map(o => <option key={o}>{o}</option>)}
                                                    </select>
                                                    {errors.delai && <span className="auto-error">{errors.delai}</span>}
                                                </div>
                                            </div>
                                            <div className="auto-form-group">
                                                <label>{t('automatisation.message_label')}</label>
                                                <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder={t('automatisation.message_placeholder')} />
                                            </div>
                                            {status === 'error' && <p className="auto-form-error">{t('automatisation.error')}</p>}
                                            <div className="auto-form-nav">
                                                <button type="button" className="auto-back-btn" onClick={() => setStep(2)}>← {t('form.back')}</button>
                                                <button type="submit" className="auto-btn-primary" disabled={loading}>
                                                    {loading ? t('form.sending') : t('form.submit')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Automatisation;
